import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getOrders, getServices, getTimeSlots } from "../../Api/Api";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaStar,
  FaPhoneAlt,
  FaComments,
  FaArrowLeft,
  FaSyncAlt,
  FaQuestionCircle,
  FaCreditCard,
} from "react-icons/fa";
import techAvatar from "../../Assets/og_female_technician_with_logo.jpg";
import defaultServiceImg from "../../Assets/og_technician_real.png";

const OrderSuccess = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [order, setOrder] = useState(null);
  const [servicesMap, setServicesMap] = useState({});
  const [slotsMap, setSlotsMap] = useState({});
  const [, setLoading] = useState(true);

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  })();

  useEffect(() => {
    setLoading(true);

    Promise.all([
      getOrders().catch(() => []),
      getServices().catch(() => []),
      getTimeSlots().catch(() => []),
    ])
      .then(([allOrders, servicesList, slotsList]) => {
        // Map services list
        const srvMap = {};
        if (Array.isArray(servicesList)) {
          servicesList.forEach((s) => {
            srvMap[s.id] = s;
          });
        }
        setServicesMap(srvMap);

        // Map timeslots
        const slotMap = {};
        if (Array.isArray(slotsList)) {
          slotsList.forEach((slot) => {
            slotMap[slot.id] = slot;
          });
        }
        setSlotsMap(slotMap);

        // Find matched order
        if (id && Array.isArray(allOrders)) {
          const matched = allOrders.find((o) => String(o.id) === String(id));
          setOrder(matched || (allOrders.length > 0 ? allOrders[0] : null));
        } else if (Array.isArray(allOrders) && allOrders.length > 0) {
          setOrder(allOrders[0]);
        }

        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load order details", err);
        setLoading(false);
      });
  }, [id]);

  function formatTimeToIST(timeStr) {
    if (!timeStr) return "10:00 AM – 02:00 PM";
    try {
      const date = new Date(`1970-01-01T${timeStr}Z`);
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      });
    } catch {
      return timeStr;
    }
  }

  // Real or Fallback Data
  const orderNumber = order ? `OG-${order.id}` : "OG-8472";
  const serviceIds = String(order?.services || "").split(",").filter(Boolean);
  const serviceNamesList = serviceIds.map((srvId) => servicesMap[srvId]?.name).filter(Boolean);
  
  const serviceTitle =
    serviceNamesList.length > 0
      ? serviceNamesList.join(", ")
      : order?.service_name || "Deep Home Cleaning";

  const firstSrvImg = serviceIds.length > 0 ? servicesMap[serviceIds[0]]?.image : null;
  const imageSrc = firstSrvImg
    ? firstSrvImg.startsWith("http")
      ? firstSrvImg
      : `http://skishore.in/api/public/${firstSrvImg}`
    : defaultServiceImg;

  const dateStr = order?.delivery_date || order?.created_at?.split(" ")[0] || "Thursday, Oct 24, 2026";
  const slotObj = order?.timeslot_id ? slotsMap[order.timeslot_id] : null;
  const slotTimeStr = slotObj ? formatTimeToIST(slotObj.start_time) : "10:00 AM – 02:00 PM";

  const fullAddress =
    order?.address ||
    [order?.street, order?.landmark].filter(Boolean).join(", ") ||
    localStorage.getItem("userLocation") ||
    "123 Azure Avenue, Apt 4B, Vellore, Tamil Nadu";

  const totalAmount = Number(order?.total || order?.sub_total || 147.0).toFixed(2);
  const isCompleted = order?.status === "completed" || order?.status === "delivered";
  const isConfirmed = order?.status === "confirmed" || isCompleted;
  const supportEmail = "contact@ourgenie.net";
  const customerName = order?.user_name || user?.name || "Customer";
  const appointmentDetails = `${dateStr} at ${slotTimeStr}`;
  const emailBody = (request) => encodeURIComponent(
    `Hello Our Genie Support,\n\nI would like to ${request} my booking.\n\nOrder: ${orderNumber}\nService: ${serviceTitle}\nAppointment: ${appointmentDetails}\nCustomer: ${customerName}\nAddress: ${fullAddress}\nTotal: Rs. ${totalAmount}\n\nPlease confirm the next steps.\n\nThank you,\n${customerName}`
  );
  const rescheduleMail = `mailto:${supportEmail}?subject=${encodeURIComponent(`Reschedule request - ${orderNumber}`)}&body=${emailBody("reschedule")}`;
  const cancelMail = `mailto:${supportEmail}?subject=${encodeURIComponent(`Cancellation request - ${orderNumber}`)}&body=${emailBody("cancel")}`;

  return (
    <div className="bg-[#F8F9FA] min-h-screen py-10 px-4 sm:px-6 lg:px-12 lg:py-24">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* TOP BREADCRUMB & BACK NAVIGATION BAR */}
        <div className="flex items-center justify-between text-xs text-gray-500 font-semibold">
          <nav className="flex items-center gap-2">
            <Link to="/" className="hover:text-black">Home</Link>
            <span>›</span>
            <Link to="/orders" className="hover:text-black">My Orders</Link>
            <span>›</span>
            <span className="text-black font-bold">Order #{orderNumber}</span>
          </nav>

          <button
            onClick={() => navigate("/orders")}
            className="flex items-center gap-1.5 text-gray-700 font-bold hover:text-[#0D004C] transition"
          >
            <FaArrowLeft size={12} />
            <span>Back to My Orders</span>
          </button>
        </div>

        {/* ORDER TITLE */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
          Order Details
        </h1>

        {/* STATUS BANNER CARD */}
        <div className="bg-[#F0F4FF] border-l-4 border-[#0D004C] p-5 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-[#0D004C] text-[#FFB800] flex items-center justify-center shrink-0 shadow">
            <FaSyncAlt className="animate-spin text-sm" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-[#0D004C]">
              {isConfirmed ? "Service Scheduled & Confirmed" : "Service in Progress"}
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              The professional is currently assigned and working on your request.
            </p>
          </div>
        </div>

        {/* TWO-COLUMN MAIN CONTENT CONTAINER */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* LEFT COLUMN (2/3 width) */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* 1. Service Details Card */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] space-y-4">
              <h3 className="text-base font-bold text-gray-900">Service Details</h3>
              <div className="flex flex-col sm:flex-row items-center gap-5">
                <div className="w-full sm:w-44 h-32 rounded-2xl overflow-hidden bg-gray-100 shrink-0">
                  <img
                    src={imageSrc}
                    alt={serviceTitle}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-2 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-xl font-bold text-gray-900">{serviceTitle}</h4>
                    <span className="bg-gray-100 text-gray-700 text-[10px] font-extrabold px-3 py-1 rounded-full uppercase">
                      Standard Package
                    </span>
                    <span className="text-xs font-bold text-gray-500">Qty: 1</span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Thorough service inspection and execution by background-verified professionals with official equipment.
                  </p>
                </div>
              </div>
            </div>

            {/* 2. Appointment & Location Card */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <h3 className="text-base font-bold text-gray-900">Appointment</h3>
                <div className="flex items-start gap-3">
                  <FaCalendarAlt className="text-gray-400 mt-1 shrink-0" size={16} />
                  <div>
                    <div className="text-xs font-bold text-gray-900">{dateStr}</div>
                    <div className="text-[11px] text-gray-400 font-medium">{slotTimeStr}</div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-base font-bold text-gray-900">Location</h3>
                <div className="flex items-start gap-3">
                  <FaMapMarkerAlt className="text-gray-400 mt-1 shrink-0" size={16} />
                  <div>
                    <div className="text-xs font-bold text-gray-900">{order?.user_name || user?.name || "Primary Residence"}</div>
                    <div className="text-[11px] text-gray-400 font-medium">{fullAddress}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Professional Assigned Card */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] space-y-4">
              <h3 className="text-base font-bold text-gray-900">Professional Assigned</h3>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <div className="w-14 h-14 rounded-full overflow-hidden border border-gray-200 shadow-sm shrink-0">
                    <img src={techAvatar} alt="Technician" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-gray-900">Sarah Jenkins</h4>
                    <div className="flex items-center gap-1 text-xs text-gray-600 font-semibold mt-0.5">
                      <FaStar className="text-[#FFB800]" size={12} />
                      <span>4.9 (120 reviews)</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <a
                    href="https://wa.me/919036011861"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 sm:flex-none border border-gray-200 hover:border-[#0D004C] text-gray-800 font-bold text-xs px-4 py-2.5 rounded-xl transition flex items-center justify-center gap-2 shadow-sm"
                  >
                    <FaComments className="text-gray-500" />
                    <span>Message</span>
                  </a>
                  <a
                    href="tel:9036011861"
                    className="flex-1 sm:flex-none border border-gray-200 hover:border-[#0D004C] text-gray-800 font-bold text-xs px-4 py-2.5 rounded-xl transition flex items-center justify-center gap-2 shadow-sm"
                  >
                    <FaPhoneAlt className="text-gray-500" size={11} />
                    <span>Call</span>
                  </a>
                </div>
              </div>
            </div>

            {/* 4. Service Timeline Card */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] space-y-6">
              <h3 className="text-base font-bold text-gray-900">Service Timeline</h3>

              <div className="space-y-6 relative pl-6 border-l-2 border-gray-100 ml-2">
                <div className="relative">
                  <div className="absolute -left-[31px] top-0.5 w-4 h-4 rounded-full bg-[#0D004C] border-2 border-white ring-2 ring-[#0D004C]" />
                  <h4 className="text-xs font-bold text-gray-900">Order Placed</h4>
                  <p className="text-[11px] text-gray-400 font-medium">{dateStr}</p>
                </div>

                <div className="relative">
                  <div className="absolute -left-[31px] top-0.5 w-4 h-4 rounded-full bg-[#0D004C] border-2 border-white ring-2 ring-[#0D004C]" />
                  <h4 className="text-xs font-bold text-gray-900">Professional Assigned</h4>
                  <p className="text-[11px] text-gray-400 font-medium">{dateStr} • {slotTimeStr}</p>
                </div>

                <div className="relative">
                  <div className="absolute -left-[31px] top-0.5 w-4 h-4 rounded-full bg-[#FFB800] border-2 border-white ring-2 ring-[#FFB800]" />
                  <h4 className="text-xs font-bold text-gray-900">Service In Progress</h4>
                  <p className="text-[11px] text-gray-400 font-medium">Active Now</p>
                </div>

                <div className="relative">
                  <div className="absolute -left-[31px] top-0.5 w-4 h-4 rounded-full bg-gray-200 border-2 border-white" />
                  <h4 className="text-xs font-bold text-gray-400">Completed</h4>
                  <p className="text-[11px] text-gray-300 font-medium">Pending Final Check</p>
                </div>
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN (1/3 width) */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* 1. Payment Summary Card */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] space-y-5">
              <h3 className="text-lg font-bold text-gray-900">Payment Summary</h3>

              <div className="space-y-3 text-xs font-medium">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-bold text-gray-900">₹{totalAmount}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Taxes & Fees</span>
                  <span className="font-bold text-gray-900">₹0.00</span>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 flex justify-between items-end">
                <span className="text-sm font-bold text-gray-900">Total Paid</span>
                <span className="text-2xl font-black text-gray-900">₹{totalAmount}</span>
              </div>

              {/* Paid Badge */}
              <div className="bg-blue-50/70 border border-blue-100 p-3.5 rounded-2xl flex items-center gap-3 text-xs text-blue-950 font-bold">
                <FaCreditCard className="text-blue-600" size={16} />
                <div>
                  <div className="capitalize">{order?.payment_method === "cod" ? "Cash on Delivery" : "Paid Online"}</div>
                  <div className="text-[10px] text-blue-600 font-normal">Ref: #{orderNumber}</div>
                </div>
              </div>
            </div>

            {/* 2. Actions Card (Only shown for active/pending orders) */}
            {!isCompleted && (
              <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] space-y-3">
                <a
                  href={rescheduleMail}
                  className="flex w-full items-center justify-center rounded-xl bg-[#0D004C] py-3.5 text-xs font-bold text-white shadow-md transition hover:bg-[#16007A]"
                >
                  Reschedule
                </a>

                <a
                  href={cancelMail}
                  className="flex w-full items-center justify-center rounded-xl border border-red-500 py-3.5 text-xs font-bold text-red-600 transition hover:bg-red-50"
                >
                  Cancel Booking
                </a>
              </div>
            )}

            {/* 3. Need Help Card */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] space-y-3">
              <h3 className="text-base font-bold text-gray-900">Need Help?</h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Have questions about your order or need to make changes?
              </p>

              <div className="pt-2 space-y-2 text-xs font-bold text-gray-800">
                <a
                  href="https://wa.me/919036011861"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-[#0D004C] transition"
                >
                  <FaComments className="text-[#0D004C]" />
                  <span>Chat with Support</span>
                </a>
                <div className="flex items-center gap-2 hover:text-[#0D004C] cursor-pointer transition">
                  <FaQuestionCircle className="text-[#0D004C]" />
                  <span>View FAQs</span>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;